import type { Round, InputValue } from '../../types';
import { isNotDefaultValue } from '../math/spadesMath';

const entered = (value: InputValue | undefined) =>
  value !== undefined && isNotDefaultValue(value);

export function teamBidsEntered(team: Round['team1BidsAndMade']): boolean {
  return entered(team.p1Bid) && entered(team.p2Bid);
}

/** Legacy rounds infer made from completed bids or an existing made value. */
export function isRoundMadePhase(round: Round | null | undefined): boolean {
  if (!round?.team1BidsAndMade || !round?.team2BidsAndMade) return false;
  const teams = [round.team1BidsAndMade, round.team2BidsAndMade];
  if (!teams.some((team) => [team.p1Bid, team.p2Bid].some(entered)))
    return false;
  if (teams.every(teamBidsEntered)) return true;
  if (round.phase) return round.phase === 'made';
  return teams.some((team) => [team.p1Made, team.p2Made].some(entered));
}

/** Persist the transition in the same update as the fourth bid, before any undo. */
export function withRoundPhase(round: Round, previous?: Round): Round {
  if (!round.team1BidsAndMade || !round.team2BidsAndMade) return round;
  const hasBid = [round.team1BidsAndMade, round.team2BidsAndMade].some((team) =>
    [team.p1Bid, team.p2Bid].some(entered),
  );
  if (hasBid && (isRoundMadePhase(round) || isRoundMadePhase(previous))) {
    return { ...round, phase: 'made' };
  }
  // Remember an explicit reset so retained made values cannot re-trigger
  // the legacy inference as bids are entered again.
  const hasMade = [round.team1BidsAndMade, round.team2BidsAndMade].some(
    (team) => [team.p1Made, team.p2Made].some(entered),
  );
  if (round.phase || (!hasBid && hasMade)) return { ...round, phase: 'bid' };
  return round;
}
